import React, { useEffect, useRef, useState } from "react";

// * Helpers
import cn from "classnames";

// * Hooks
import { useParams } from "react-router-dom";
import { useChannelAuth } from "../../../../hooks/useChannelAuth";
import useGetBroadcastStatus from "../../../../hooks/useGetBroadcastStatus";
import { useCountDown } from "../../../../hooks/useCountDown";
import { useSocketApi } from "../../../../hooks/useSocketApi";

// * Props
import { DropdownMenuItem } from "../../../DropdownMenu/DropdownMenu.interface";
import {
  Channel,
  Platform,
  ChannelError,
  User,
  useGetBroadcastQuery,
  useGetUserQuery,
  useRefreshChannelMutation
} from "../../../../features/api/apiSlice";

// * Components
import { Switch } from "../../../Switch";
import StreamStatus from "../../../StreamStatus";
import Icon from "../../../Icon";
import PlatformIcon from "../../../PlatformIcon";
import DropdownMenu from "../../../DropdownMenu";
import ChannelErrorMessage from "../ChannelError/ChannelErrorMessage";
import { Checkbox } from "../../../Checkbox";

// * Local
import { ConnectedChannelProps } from './ConnectedChannel.interface';
import styles from './ConnectedChannel.module.css';

const getChannelError = (channel: Channel, user?: User): ChannelError | undefined => {
  const isUnsupportedPlatform = !channel.platform && !user?.tariff?.custom_links;
  return isUnsupportedPlatform ? ChannelError.UNSUPPORTED_PLATFORM : channel.error;
};

const ConnectedChannel: React.FC<ConnectedChannelProps> = ({
  channel,
  status = "disabled",
  onToggleEnabled,
  onEdit,
  onDelete,
  onCheckboxChange,
  showCheckbox = false,
  isChecked = false,
  studio
}) => {
  const params = useParams<{id: string}>();
  const channelRef = useRef<HTMLDivElement>(null);
  const { data: user } = useGetUserQuery();
  const broadcastId = studio?.broadcast.id ?? Number(params.id);
  const [refreshChannel, {data: refreshResponse, isLoading: isRefreshQueryLoading }] = useRefreshChannelMutation();
  const {data: broadcast} = useGetBroadcastQuery(broadcastId);
  const { isOffline: isBroadcastOffline } = useGetBroadcastStatus(broadcastId);
  const [nextRestartTime, setNextRestartTime] = useState(channel.next_restart_time);
  const {
    countDownView: refreshCountDownValue,
    isCountdownContinue: isRefreshing
  } = useCountDown({ startAt: nextRestartTime });

  const { handleLoginViaPlatform } = useChannelAuth(broadcastId);
  const channelError = getChannelError(channel, user);
  const isChannelError = channelError && channelError !== ChannelError.STREAM_BITRATE_WARNING;

  const handleUpdateToken = (platform?: Platform) => {
    if (!platform) return;

    handleLoginViaPlatform(platform);
  };

  const channelRefreshHandler = () => {
    if (!broadcast) return;

    const channelId = channel.id;
    const broadcastId = broadcast.id;

    refreshChannel({ broadcastId, channelId });
  };

  const getChannelDropdownItems = (channel: Channel): DropdownMenuItem[] => {
    if (channelError && channelError === ChannelError.TOKEN_AUTH_ERROR) {
      return [
        {
          action: () => handleUpdateToken(channel.platform),
          iconName: "refresh",
          label: "Обновить токен"
        }
      ];
    } else {
      const isRefreshDisabled = isRefreshing || isRefreshQueryLoading || isBroadcastOffline || !channel.enabled;

      return [
        {
          action: () => onEdit?.(channel),
          iconName: "gear",
          label: "Настройки"
        },
        {
          action: channelRefreshHandler,
          iconName: "refresh",
          label: isRefreshing ? refreshCountDownValue : "Перезапустить",
          disabled: isRefreshDisabled,
          ...((isRefreshDisabled) && {
            className: styles["dropdown__item_no-click"],
            iconClassName: (isRefreshing || isRefreshQueryLoading) ? styles["dropdown__item_rotate"] : "",
          }),
        },
        {
          action: () => onDelete?.(channel),
          iconName: "trash-bin",
          label: "Удалить",
          className: styles["dropdown__item_dangerous"]
        }
      ];
    }
  };

  useSocketApi({
    broadcastId: broadcast?.id,
    channelId: channel.id,
    onNextTemplateRestartTime: ({ next_restart_time }) => {
      setNextRestartTime(next_restart_time);
    }
  });

  useEffect(() => {
    if (isChannelError && channel.enabled && onToggleEnabled) {
      onToggleEnabled(channel);
    }
  }, [channel.error, channel.enabled]);

  useEffect(() => {
    if (!refreshResponse) return;

    setNextRestartTime(refreshResponse.next_restart_time);
  }, [refreshResponse]);

  return (
    <div
      className={cn(styles.channel, { [styles.channel_error]: isChannelError, [styles["channel_with-checkbox"]]: showCheckbox })}
      ref={channelRef}
    >
      {showCheckbox && (
        <div className={styles.channel__checkbox}>
          <Checkbox
            onChange={onCheckboxChange}
            value={isChecked}
          />
        </div>
      )}

      {isChannelError && (
        <ChannelErrorMessage
          error={channelError!}
          platform={channel.platform}
          onUpdateToken={handleUpdateToken}
          onDelete={() => onDelete?.(channel)}
        />
      )}

      <div className={styles.channel__platform}>
        <PlatformIcon platform={channel.platform} />
      </div>

      <div className={styles.channel__name}>
        {channel.name}
      </div>

      <div className={styles.channel__buttons}>

        {channel.enabled && channel.transcode_preset && Boolean(user?.tariff.max_active_transcodes) && !studio &&
          <div className={styles["channel__transcode"]}>
            <Icon name="transcode" />
          </div>
        }

        <StreamStatus isStatusText={false} status={status} />

        <Switch
          onChange={() => onToggleEnabled?.(channel)}
          checked={channel.enabled}
          disabled={isChannelError}
        />

        <DropdownMenu
          items={getChannelDropdownItems(channel)}
        />
      </div>
    </div>
  );
};

export default ConnectedChannel;